import { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";

/**
 * Normalizes role string for admin / superadmin checks.
 */
function normalizeRole(role) {
  if (role == null) return "";
  return String(role).toLowerCase().replace(/\s+/g, "");
}

/**
 * Department filter for Department module pages (Teachers, Courses, Subjects, Classes).
 * - Admin / Super Admin: default "all" (null); optional department narrows results.
 * - Other scoped users (e.g. HOD with departmentId): default to their department.
 */
export function useDepartmentModuleFilter() {
  const { selectedInstitute } = useSelector((state) => state.instituteContext || {});
  const authUser = useSelector((state) => state.auth?.user);

  const [currentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  });

  const role = normalizeRole(authUser?.role ?? currentUser?.role);
  const isAdminOrSuperAdmin = role === "admin" || role === "superadmin";

  const userDepartmentId =
    currentUser?.departmentId ?? authUser?.departmentId ?? null;

  const effectiveInstituteId =
    currentUser?.instituteId ?? selectedInstitute?.id ?? null;

  const [departmentFilter, setDepartmentFilter] = useState(null);

  useEffect(() => {
    if (!isAdminOrSuperAdmin && userDepartmentId) {
      setDepartmentFilter(Number(userDepartmentId));
    }
  }, [isAdminOrSuperAdmin, userDepartmentId]);

  const showAllDepartmentsOption = true;

  /** Query params to merge into list API calls (omit departmentId when "all" for institute admins). */
  const departmentApiParams = useMemo(() => {
    const depNum = Number(departmentFilter);
    if (Number.isFinite(depNum) && depNum > 0) {
      return { departmentId: depNum };
    }
    return {};
  }, [departmentFilter]);

  const resetToDefault = useCallback(() => {
    if (!isAdminOrSuperAdmin && userDepartmentId) {
      setDepartmentFilter(Number(userDepartmentId));
    } else {
      setDepartmentFilter(null);
    }
  }, [isAdminOrSuperAdmin, userDepartmentId]);

  return {
    departmentFilter,
    setDepartmentFilter,
    showAllDepartmentsOption,
    departmentApiParams,
    effectiveInstituteId,
    resetToDefault,
  };
}
